/* eslint-disable react/require-default-props */
/* eslint-disable tailwindcss/no-arbitrary-value */
import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  arrayOf,
  bool,
  client,
  number,
  params,
  shape,
  string,
  useDataModel,
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useConfigService, useStore } from '@thd-nucleus/experience-context';
import { useLifeCycleEventBus } from '@thd-olt-functional/utils';
import { Alert, Button } from '@one-thd/sui-atomic-components';
import { AddToCart } from '@thd-olt-component-react/add-to-cart';
import { useCalculatorLocalStorage } from '@thd-olt-component-react/calculator';
import { podFulFillmentUtils } from '@thd-olt-component-react/fulfillment';
import { VerifyQuantityDrawer } from '@thd-olt-component-react/flooring-picker';
import { useImpression } from '@thd-olt-component-react/impression';
import {
  GccCarpetSamCta,
  ScheduleAMeasureCta,
} from '@thd-olt-component-react/home-services-cta-buttons';
import PianoTabContext from '../../context/PianoContext';
import { ServicesButton } from './ServicesButton';
import {
  ADD_TO_CART,
  BUILD_AND_BUY,
  CHOOSE_OPTIONS,
  CLOPAY_URL,
  GET_ESTIMATE,
  NON_RETURNABLE_MESSAGE,
  PRODUCT_POD_ATC,
  PRODUCT_POD_IRG_ATC,
  PRODUCT_POD_V7_CLICK,
  REQUEST_A_QUOTE,
  SCHEDULE_A_CONSULTATION,
  SCHEDULE_A_MEASURE,
  VIEW_DETAILS,
  VIEW_PACKAGE,
  DESIGN_AND_PURCHASE
} from '../../constants';

import {
  getCustomUrlWithAnalytics,
  isBrioProduct,
  isBuildAndBuyProduct,
  isCustomKitchenCabinetProduct,
  isHDDCSku,
  isLiveGoodsOOSProduct,
  isDesignAndPurchaseProduct,
} from '../../productPodUtils';

import {
  getCartOptions,
  getCartReqParams,
  getMisship,
  getShowBopisOverlay,
  isAppliance,
} from './product-atc-utils';

const { isATCEnabled, isFulfillable, isProductOutOfStockOnline } = podFulFillmentUtils;

const PlaceholderATC = ({ isSmallButton }) => {
  return <AddToCart loading isSmallButton={isSmallButton}>Add to Cart</AddToCart>;
};

export const ProductATC = ({
  className = null,
  checkGeneric = true,
  disabled = false,
  doNotShowChooseYourOptions = false,
  dpdNewTab = false,
  hasInStoreFilter = false,
  hideATC = false,
  itemId = '',
  noATCFulfillment = false,
  onCartFailure = () => { },
  onCartSuccess = () => { },
  onClick = () => { },
  useParentWindowATC = false,
  sharedSection = '',
  showDisabledATC = false,
  showProjectQuantity = false,
  showReturnable = false,
  showSoldOutMessage = false,
  silent = false,
  sponsoredValues = null,
  staticQuantity = 0,
  subscriptionInfo = {},
  target = '',
  quantity: editableQuantity = 1,
  storeId = null,
  variant = 'secondary',
  isB2BPLP = false,
  isSmallButton = false
}) => {

  const { keyLabel } = useContext(PianoTabContext);
  const { channel, hosts, dataType } = useContext(ExperienceContext);
  const hideGCCComponentFS = useConfigService('fs:hideGCCForInstalledCarpet');
  const skipProductPodMisship = useConfigService('fs:skipProductPodMisship') || false;

  const { additionalData = {} } = useImpression({
    data: {
      id: itemId,
      component: 'ProductATC',
      name: 'ProductATC',
      position: 0
    }
  });

  const { parent = '', section, position } = additionalData;

  const store = useStore();

  let deliveryZipCode;
  if (
    typeof window !== 'undefined'
    && window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails
  ) {
    deliveryZipCode = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails()?.zipcode;
  }

  const { data, error, loading } = useDataModel('product', {
    variables: {
      itemId,
      storeId: storeId || store.storeId
    }
  });

  const product = data?.product;
  const {
    info, identifiers, pricing, taxonomy, bundleFlag
  } = product || {};
  const { isBuryProduct = false } = info || {};

  const { toggleOn: flooringPickerToggleOn } = useLifeCycleEventBus(
    'flooringPicker.flooringPicker_toggle',
    true
  );

  const { projectQuantity } = useCalculatorLocalStorage({
    label: taxonomy?.breadCrumbs?.[0]?.label,
    uomObj: {
      sizeValue: pricing?.alternate?.unit?.unitsPerCase,
      caseUnitOfMeasure: pricing?.alternate?.unit?.caseUnitOfMeasure,
    },
    toggleOn: flooringPickerToggleOn && showProjectQuantity
  });

  const isInstalledCarpet = taxonomy?.breadCrumbs?.some((breadCrumb) => breadCrumb.label === 'Installed Carpet');
  const shouldHideGCCComponent = (hideGCCComponentFS && isInstalledCarpet);

  const isApplianceFromCatalog = isAppliance(product) && product?.dataSources === 'catalog';
  const isDisabled = isApplianceFromCatalog ? false : (!bundleFlag && !isATCEnabled(product)) || disabled;

  const getAtcLabel = () => {
    if (info?.customerSignal?.previouslyPurchased && !bundleFlag) return 'Buy It Again';
    // eslint-disable-next-line max-len
    if (
      isProductOutOfStockOnline(product)
      && !isAppliance(product)
      && !showSoldOutMessage
      && !bundleFlag
    ) return 'Out Of Stock';
    if (isDisabled && showSoldOutMessage && !bundleFlag) return 'Sold Out';
    if (subscriptionInfo?.isOptIn && !bundleFlag) return 'Subscribe';
    return 'Add to Cart';
  };

  const atcLabel = getAtcLabel();

  useEffect(() => {
    if (isB2BPLP && product && atcLabel === 'Add to Cart' && typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS?.trigger) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('product-pod.atc-button.rendered', {
        podAnchorSku: itemId
      });
    }
  }, [isB2BPLP, product?.itemId, atcLabel, itemId]);

  if (hideATC) return null;

  const quantity = projectQuantity || staticQuantity || editableQuantity;

  const showVerifyQuatityCTA = projectQuantity;

  const isMobile = channel === 'mobile';

  if (loading || (typeof data === 'undefined' && !error)) {
    return <PlaceholderATC isSmallButton={isMobile && isSmallButton} />;
  }

  if (!product || (!bundleFlag && product?.info?.productSubType?.name === 'HDQC')) return null;

  const { scheduleAMeasure = false, gccCarpetDesignAndOrderEligible = false } = product?.installServices || {};

  const isRequestAQuoteEligible = isBrioProduct(product);
  const isScheduleAConsultationEligible = isCustomKitchenCabinetProduct(product);

  const showViewDetailsBtn = isLiveGoodsOOSProduct(product) || isHDDCSku(product);
  const showBuildAndBuyButton = isBuildAndBuyProduct(product);
  const showDesignAndPurchaseButton = isDesignAndPurchaseProduct(product);
  const showRequestAQuoteButton = isRequestAQuoteEligible;
  const showScheduleAConsultationButton = isScheduleAConsultationEligible;
  const showChooseYourOptionsButton = !doNotShowChooseYourOptions && checkGeneric && product?.info?.isGenericProduct;

  const isClopayAndBrioSku = data?.product?.identifiers?.brandName === 'Clopay' && data?.product?.info?.isBrioSku;
  const showClopayRequestAQuoteButton = showRequestAQuoteButton && isClopayAndBrioSku;

  const showSimilarButton = isBuryProduct && !isFulfillable(product);

  const shouldHide = !scheduleAMeasure
    && !bundleFlag
    && !showDisabledATC
    && isDisabled
    && !showBuildAndBuyButton
    && !showViewDetailsBtn
    && !showRequestAQuoteButton
    && !showScheduleAConsultationButton
    && !showDesignAndPurchaseButton
    && showSimilarButton;

  if (shouldHide) return null;

  let cartOptionsSharedSection = sharedSection;

  const returnMessage = showReturnable && /non-returnable/i.test(product?.info?.returnable)
    ? NON_RETURNABLE_MESSAGE
    : null;

  const cartOptions = getCartOptions({
    channel,
    hidden: !!silent,
    sharedSection: cartOptionsSharedSection,
    misship: getMisship({ product }),
    skipProductPodMisship
  });
  const cartReqParams = getCartReqParams({
    store,
    quantity,
    deliveryZipCode,
    product,
    channel,
    noATCFulfillment,
    hasInStoreFilter,
    subscriptionInfo,
  });
  const showBopisOverlay = getShowBopisOverlay({ product, cartReqParams });
  const { canonicalUrl, productType = '' } = identifiers || {};

  const productUrl = getCustomUrlWithAnalytics({
    productType,
    blindsHost: hosts?.customBlinds,
    canonicalUrl,
    info,
    sponsoredValues,
  });

  const servicesURL = info?.productSubType?.link;

  const cartClicked = (event) => {
    const action = ADD_TO_CART;

    let eventName = PRODUCT_POD_ATC;

    const isTableView = dataType === 'expanded-product-data';

    let eventData = {
      podAction: action,
      podAnchorSku: itemId,
      isTableView,
      target,
      parent: parent.toLowerCase(),
      section,
      podKey: keyLabel.toLowerCase()
    };

    if (parent === 'IRG') {
      eventName = PRODUCT_POD_IRG_ATC;

      eventData = {
        sku: itemId,
        displayPosition: position,
        irgSectionName: section,
      };
    }

    if (parent !== 'DPD' && parent !== 'PIP') {
      if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
        LIFE_CYCLE_EVENT_BUS.trigger(eventName, eventData);

        if (parent === 'plp') {
          setTimeout(() => {
            LIFE_CYCLE_EVENT_BUS.trigger(PRODUCT_POD_V7_CLICK, eventData);
          }, 0);
        }
      }
    }

    if (onClick) {
      onClick(event, product, action);
    }
    if (event?.nativeEvent) {
      event.nativeEvent._podAnalyticsHandled = true;
    }
  };

  const clickEventData = {
    podAnchorSku: itemId,
    target,
    parent,
    podKey: keyLabel.toLowerCase(),
  };

  const triggerAnalyticsEvent = (action, event) => {
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
      setTimeout(() => {
        LIFE_CYCLE_EVENT_BUS.trigger(PRODUCT_POD_V7_CLICK, {
          podAction: action,
          ...clickEventData,
        });
      }, 0);
    }

    if (onClick) {
      onClick(event, product);
    }
    if (event?.nativeEvent) {
      event.nativeEvent._podAnalyticsHandled = true;
    }
  };

  const onServicesButtonClick = (buttonName, event) => {
    triggerAnalyticsEvent(buttonName.toLowerCase(), event);

    const urlParts = servicesURL?.split('/form')?.[0]?.split('/');
    const formName = urlParts?.[urlParts?.length - 1];

    const eventData = {
      eventName: buttonName.toLowerCase(),
      formName: formName.toLowerCase(),
    };

    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
      LIFE_CYCLE_EVENT_BUS.trigger('product-pod-v7.services-button.click', eventData);
    }
  };

  const linkButtonClassnames = `sui-btn sui-btn-secondary sui-btn-secondary-focus
      sui-w-full sui-btn-secondary-active sui-btn-secondary-hover
      sui-font-bold sui-text-base sui-leading-normal sui-tracking-normal
      sui-normal-case sui-line-clamp-unset sui-font-normal hover:sui-no-underline`;

  const smallButtonStyles = isMobile && isSmallButton ? { fontSize: '14px', height: '30px' } : undefined;

  if (bundleFlag) {
    return (
      <Button
        variant="secondary"
        className={linkButtonClassnames}
        href={productUrl}
        onClick={(event) => triggerAnalyticsEvent(VIEW_PACKAGE.toLowerCase(), event)}
        target={target}
        fullWidth
        style={smallButtonStyles}
      >
        {VIEW_PACKAGE}
      </Button>
    );
  }

  if (scheduleAMeasure && gccCarpetDesignAndOrderEligible && !bundleFlag && !shouldHideGCCComponent) {
    return (
      <GccCarpetSamCta
        itemId={itemId}
        onClick={(event) => triggerAnalyticsEvent(GET_ESTIMATE.toLowerCase(), event)}
        hideLabel
        target="_self"
        style={smallButtonStyles}
      />
    );
  }

  if (!bundleFlag) {
    switch (true) {
    case scheduleAMeasure:
      return (
        <ScheduleAMeasureCta
          itemId={itemId}
          hideContactInfo
          onClick={(event) => triggerAnalyticsEvent(SCHEDULE_A_MEASURE.toLowerCase(), event)}
          style={smallButtonStyles}
        />
      );

    case showBuildAndBuyButton:
      return (
        <Button
          variant="secondary"
          className={linkButtonClassnames}
          href={productUrl}
          target={target}
          onClick={(event) => triggerAnalyticsEvent(BUILD_AND_BUY, event)}
          fullWidth
          style={smallButtonStyles}
        >
          {BUILD_AND_BUY}
        </Button>
      );

    case showDesignAndPurchaseButton:
      return (
        <Button
          variant="secondary"
          className={linkButtonClassnames}
          href={productUrl}
          target={target}
          onClick={(event) => triggerAnalyticsEvent(DESIGN_AND_PURCHASE, event)}
          fullWidth
          style={smallButtonStyles}
        >
          {DESIGN_AND_PURCHASE}
        </Button>
      );

    case showViewDetailsBtn:
      return (
        <>
          <Button
            variant="secondary"
            href={productUrl}
            target={target}
            data-testid="bttn__view-details"
            fullWidth
            style={smallButtonStyles}
          >
            {VIEW_DETAILS}
          </Button>
        </>
      );

    case showRequestAQuoteButton:
      return showClopayRequestAQuoteButton ? (
        <Button
          variant="secondary"
          data-testid="bttn__request-a-quote"
          href={CLOPAY_URL}
          onClick={(event) => onServicesButtonClick(REQUEST_A_QUOTE, event)}
          target="_blank"
          fullWidth
          style={smallButtonStyles}
        >
          {REQUEST_A_QUOTE}
        </Button>
      ) : (
        <ServicesButton
          name={REQUEST_A_QUOTE}
          servicesURL={servicesURL}
          onClick={(event) => onServicesButtonClick(REQUEST_A_QUOTE, event)}
          style={smallButtonStyles}
        />
      );

    case showScheduleAConsultationButton:
      return (
        <ServicesButton
          name={SCHEDULE_A_CONSULTATION}
          servicesURL={servicesURL}
          onClick={(event) => onServicesButtonClick(SCHEDULE_A_CONSULTATION, event)}
          style={smallButtonStyles}
        />
      );

    case showChooseYourOptionsButton: {
      const newTab = dpdNewTab && !target ? '_blank' : target;
      return (
        <Button
          variant="secondary"
          className={linkButtonClassnames}
          href={productUrl}
          onClick={(event) => onClick(event, product)}
          target={newTab}
          fullWidth
          style={smallButtonStyles}
        >
          {CHOOSE_OPTIONS}
        </Button>
      );
    }

    default:
      break;
    }
  }

  if (showVerifyQuatityCTA) {
    return (
      <VerifyQuantityDrawer
        storeId={storeId}
        cartOptions={cartOptions}
        cartReqParams={cartReqParams}
        disabled={isDisabled}
        atcLabel={atcLabel}
        projectQuantity={projectQuantity}
        variant="secondary"
        style={smallButtonStyles}
      />
    );
  }

  return (
    <>
      <AddToCart
        className={className}
        checkGeneric={checkGeneric}
        showDisabledATC={showDisabledATC}
        showReturnable={showReturnable}
        cartOptions={cartOptions}
        cartReqParams={cartReqParams}
        disabled={isDisabled}
        onClick={cartClicked}
        useParentWindowATC={useParentWindowATC}
        variant={variant}
        showBOPISOverlay={showBopisOverlay}
        onSuccess={onCartSuccess}
        onFail={onCartFailure}
        isSmallButton={isSmallButton}
      >
        {atcLabel}
      </AddToCart>
      {returnMessage && (
        <Alert className="sui-mt-2" status="warning">
          {returnMessage}
        </Alert>
      )}
    </>
  );
};

ProductATC.propTypes = {
  /** When true, checks if sku is generic and returns "Choose Your Options" button. */
  checkGeneric: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  /** Method triggered when `checkGeneric` is true and "Choose Your Options" button clicked */
  onClick: PropTypes.func,
  /** When true, makes ATC trigger in the top window */
  useParentWindowATC: PropTypes.bool,
  /** show return messaging */
  showReturnable: PropTypes.bool,
  /** Add to Cart without overlay */
  silent: PropTypes.bool,
  subscriptionInfo: PropTypes.shape({
    frequency: PropTypes.string,
    isOptIn: PropTypes.bool,
  }),
  doNotShowChooseYourOptions: PropTypes.bool,
  dpdNewTab: PropTypes.bool,
  hasInStoreFilter: PropTypes.bool,
  itemId: PropTypes.string,
  noATCFulfillment: PropTypes.bool,
  onCartSuccess: PropTypes.func,
  onCartFailure: PropTypes.func,
  showDisabledATC: PropTypes.bool,
  hideATC: PropTypes.bool,
  sharedSection: PropTypes.string,
  showProjectQuantity: PropTypes.bool,
  showSoldOutMessage: PropTypes.bool,
  staticQuantity: PropTypes.number,
  sponsoredValues: PropTypes.shape({}),
  target: PropTypes.string,
  quantity: PropTypes.number,
  storeId: PropTypes.string,
  variant: PropTypes.string,
  isB2BPLP: PropTypes.bool,
  isSmallButton: PropTypes.bool,
};

ProductATC.displayName = 'ProductATC';

const Service = shape({
  deliveryTimeline: string(),
  deliveryDates: shape({
    startDate: string(),
    endDate: string(),
  }),
  deliveryCharge: string(),
  dynamicEta: shape({
    hours: string(),
    minutes: string(),
  }),
  hasFreeShipping: bool(),
  freeDeliveryThreshold: number({ float: true }),
  locations: arrayOf(
    shape({
      curbsidePickupFlag: bool(),
      isBuyInStoreCheckNearBy: bool(),
      distance: number({ float: true }),
      inventory: shape({
        isOutOfStock: bool(),
        isInStock: bool(),
        isLimitedQuantity: bool(),
        isUnavailable: bool(),
        quantity: number(),
        maxAllowedBopisQty: number(),
        minAllowedBopisQty: number(),
      }),
      isAnchor: bool(),
      locationId: string(),
      state: string(),
      storeName: string(),
      storePhone: string(),
      type: string(),
    })
  ),
  type: string(),
  totalCharge: number({ float: true }),
});

const installServices = params({ storeId: string(), zipCode: string() }).shape({
  scheduleAMeasure: bool(),
  gccCarpetDesignAndOrderEligible: bool(),
});
if (installServices.skip) {
  installServices.skip('skipInstallServices', true);
}

ProductATC.dataModel = {
  product: params({
    itemId: string().isRequired(),
  }).shape({
    itemId: string(),
    dataSources: string(),
    details: shape({
      installation: shape({
        serviceType: string(),
      }),
    }),
    fulfillment: client(
      params({ storeId: string(), zipCode: string() }).shape({
        anchorStoreStatus: bool(),
        anchorStoreStatusType: string(),
        backordered: bool(),
        backorderedShipDate: string(),
        bossExcludedShipStates: string(),
        excludedShipStates: string(),
        seasonStatusEligible: bool(),
        fulfillmentOptions: arrayOf(
          shape({
            type: string(),
            fulfillable: bool(),
            services: arrayOf(Service),
          })
        ),
        onlineStoreStatus: bool(),
        onlineStoreStatusType: string(),
      })
    ),
    identifiers: shape({
      brandName: string(),
      productType: string(),
      canonicalUrl: string(),
      specialOrderSku: string(),
      storeSkuNumber: string(),
    }),
    info: shape({
      customerSignal: shape({
        previouslyPurchased: bool(),
      }),
      isBuryProduct: bool(),
      isGenericProduct: bool(),
      isBrioSku: bool().client(),
      isCustomKitchenCabinet: bool().client(),
      globalCustomConfigurator: shape({
        customExperience: string(),
      }),
      productSubType: shape({
        name: string(),
        link: string(),
      }),
      returnable: string(),
    }),
    installServices,
    taxonomy: shape({
      breadCrumbs: arrayOf(
        shape({
          label: string(),
        })
      ),
    }),
  }),
};

// we are adding this for bundle product which is not available in recs apis
export const BundleDataModel = {
  product: params({
    itemId: string().isRequired(),
  }).shape({
    bundleFlag: bool(),
    bundleItems: arrayOf(
      shape({
        id: string(),
        quantity: number(),
      })
    ),
  }),
};
